import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, Clock, AlertTriangle, FileEdit, Loader2 } from 'lucide-react';

// Labels
const TYPE_LABEL = { avaliacao: 'Avaliação Inicial', evolucao: 'Evolução' };
const TEMPLATE_LABEL = {
  consultorio: 'Consultório', pilates: 'Pilates', pilates_clinico: 'Pilates Clínico',
};

const relativeTime = (iso) => {
  if (!iso) return null;
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'há instantes';
    if (m < 60) return `há ${m} minuto${m > 1 ? 's' : ''}`;
    const h = Math.floor(m / 60);
    if (h < 24) return `há ${h} hora${h > 1 ? 's' : ''}`;
    const d = Math.floor(h / 24);
    return `há ${d} dia${d > 1 ? 's' : ''}`;
  } catch { return null; }
};

const daysUntilExpiry = (iso) => {
  if (!iso) return null;
  try {
    return Math.ceil((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  } catch { return null; }
};

// Progress indicator based on status + src_confirmed + consultation_type
const progressForCard = (c) => {
  const isAvaliacao = c.consultation_type === 'avaliacao';
  if (c.status === 'draft' || !c.has_document) {
    return { dots: '○ ○ ○', label: 'Documento não gerado', tone: 'neutral' };
  }
  // status = in_review
  if (isAvaliacao && !c.src_confirmed) {
    return { dots: '● ● ○', label: 'Aguardando confirmação da Síntese', tone: 'amber' };
  }
  if (!isAvaliacao && !c.src_confirmed) {
    return { dots: '● ○ ○', label: 'Aguardando aprovação', tone: 'amber' };
  }
  return { dots: '● ● ●', label: 'Pronto para salvar', tone: 'emerald' };
};

const DraftCard = ({ draft }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const progress = progressForCard(draft);
  const days = daysUntilExpiry(draft.draft_expires_at);
  const isExpiringSoon = days != null && days >= 0 && days <= 5;
  const isExpired = days != null && days < 0;

  const dotColor = {
    neutral: isDark ? 'text-slate-400' : 'text-slate-500',
    amber: isDark ? 'text-amber-300' : 'text-amber-600',
    emerald: isDark ? 'text-emerald-300' : 'text-emerald-600',
  }[progress.tone];

  const borderColor = isExpired
    ? 'border-red-300 dark:border-red-700/50 opacity-60'
    : isExpiringSoon
      ? 'border-amber-300 dark:border-amber-700/50'
      : isDark ? 'border-slate-700' : 'border-slate-200';

  const savedOrStarted = draft.draft_saved_at
    ? `Salvo ${relativeTime(draft.draft_saved_at)}`
    : `Iniciado ${relativeTime(draft.updated_at || draft.created_at)}`;

  return (
    <div
      className={`rounded-lg border ${borderColor} ${isDark ? 'bg-slate-800/50' : 'bg-white'} p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between`}
      data-testid={`draft-card-${draft.id}`}
    >
      {/* Left */}
      <div className="min-w-0 flex-1">
        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'} truncate`} data-testid="draft-patient">
          {draft.patient_identifier || 'Paciente'}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          <Badge variant="outline" className="text-[11px]">{TYPE_LABEL[draft.consultation_type] || draft.consultation_type}</Badge>
          <Badge variant="secondary" className="text-[11px]">{TEMPLATE_LABEL[draft.template_type] || draft.template_type}</Badge>
        </div>
      </div>

      {/* Center — progress */}
      <div className="flex-1 min-w-0 md:px-4">
        <p className={`font-mono ${dotColor} text-sm`} data-testid="draft-progress-dots">{progress.dots}</p>
        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'} mt-0.5`}>
          {progress.label}
        </p>
        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'} mt-1 flex items-center gap-1`}>
          <Clock className="w-3 h-3" /> {savedOrStarted}
        </p>
      </div>

      {/* Right — action + expiration */}
      <div className="flex flex-col items-stretch md:items-end gap-1.5">
        {isExpired ? (
          <>
            <Button disabled variant="outline" size="sm" data-testid={`draft-expired-${draft.id}`}>
              Expirado
            </Button>
            <p className="text-xs text-red-500">Este rascunho foi removido automaticamente.</p>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                // DRAFT (no document yet) → resume the consultation FLOW
                // (audio recording step). The detail page is a dead-end here
                // because it only renders "Documentação ainda não gerada"
                // without any way to record audio.
                //
                // IN_REVIEW (document already generated) → go to the detail
                // page so the professional can review/edit, confirm the SRC
                // and approve as official record.
                if (draft.status === 'draft') {
                  navigate(`/consultations/new/${draft.patient_id}?consultation=${draft.id}`);
                } else {
                  navigate(`/consultations/${draft.id}`);
                }
              }}
              size="sm"
              className="bg-primary hover:bg-primary-hover"
              data-testid={`draft-continue-${draft.id}`}
            >
              Continuar <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
            {isExpiringSoon && (
              <p className="text-xs text-amber-600 dark:text-amber-300 font-medium">
                Expira em {days} dia{days !== 1 ? 's' : ''}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const DraftsPanel = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isDark } = useTheme();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.getInProgressConsultations();
        if (mounted) setDrafts(res.consultations || []);
      } catch (_) {
        if (mounted) setDrafts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const expiringSoonCount = useMemo(() => {
    return drafts.filter((d) => {
      const days = daysUntilExpiry(d.draft_expires_at);
      return days != null && days >= 0 && days <= 5;
    }).length;
  }, [drafts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
      </div>
    );
  }

  // Zero-state — render nothing
  if (drafts.length === 0) return null;

  return (
    <Card className="mb-6" data-testid="drafts-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileEdit className="w-5 h-5 text-amber-500" />
              Rascunhos em Andamento
              <Badge variant="outline" className="ml-2" data-testid="drafts-count">{drafts.length}</Badge>
            </CardTitle>
            <CardDescription>
              Documentos iniciados mas ainda não confirmados como prontuário oficial.
            </CardDescription>
          </div>
        </div>

        {expiringSoonCount > 0 && (
          <div
            className={`mt-3 flex items-start gap-2 p-3 rounded-lg border ${isDark ? 'bg-amber-950/30 border-amber-900/50 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-900'}`}
            data-testid="drafts-expiration-banner"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <p className="text-xs">
              <strong>{expiringSoonCount}</strong> rascunho{expiringSoonCount > 1 ? 's' : ''} expira{expiringSoonCount > 1 ? 'm' : ''} nos próximos 5 dias.
              Confirme para incluir no prontuário oficial ou serão excluídos automaticamente.
            </p>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {drafts.map((d) => <DraftCard key={d.id} draft={d} />)}
      </CardContent>
    </Card>
  );
};

export default DraftsPanel;
